import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, Observable, Subscription, map } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme, PopoverService } from '@fcom/ui-components';
import { LoginStatus, LoginStep, Profile, ProfileTier } from '@fcom/core-api/login';
import { loginStatus, profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { AppState, ConfigService } from '@fcom/core';
import { addClass, removeClass } from '@fcom/core/utils';
import { finShare, keepState } from '@fcom/rx';
import { LoginActions } from '@fcom/core/actions';
import { GtmService } from '@fcom/common/gtm';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';

import { JoinService, LoginService } from '../../services';
import { ElementActions, ElementTypes, GaContext, onOpenGtmEvent } from '../../../interfaces';

const STEP_CHANGE_TRANSITION_DURATION_MS = 500;

export enum LoginWidgetContext {
  PORTAL = 'portal',
  BOOKING = 'booking',
  MMB = 'mmb',
}

@Component({
  selector: 'fin-login-widget',
  templateUrl: './login-widget.component.html',
  styleUrls: ['./login-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginWidgetComponent implements OnInit {
  private store$ = inject<Store<AppState>>(Store);
  private elementRef = inject(ElementRef);
  private loginService = inject(LoginService);
  private joinService = inject(JoinService);
  private configService = inject(ConfigService);
  private popoverService = inject(PopoverService);
  private gtmService = inject(GtmService);
  private document = inject<Document>(DOCUMENT);
  private themeService = inject(FinnairThemeService);

  readonly LoginStatus = LoginStatus;
  readonly ButtonTheme = ButtonTheme;
  readonly loginStatus$: Observable<LoginStatus>;
  readonly LoginWidgetContext = LoginWidgetContext;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  // New-brand's silver/gold gradients are light (dark text), unlike legacy's dark silver/gold
  // gradients (white text) — platinum/lumo/corporate stay dark-background in both themes.
  private readonly LEGACY_DARK_BACKGROUND_TIERS = [
    ProfileTier.SILVER,
    ProfileTier.GOLD,
    ProfileTier.PLATINUM,
    ProfileTier.LUMO,
    ProfileTier.CORPORATE,
  ];
  private readonly NEW_BRAND_DARK_BACKGROUND_TIERS = [ProfileTier.PLATINUM, ProfileTier.LUMO, ProfileTier.CORPORATE];

  @Input()
  context: LoginWidgetContext = LoginWidgetContext.PORTAL;

  @Output()
  isOpenChange = new EventEmitter<boolean>();

  profile$: Observable<Profile>;
  private isOpenSubject$ = new BehaviorSubject(false);
  protected readonly isOpen$ = this.isOpenSubject$.asObservable();
  widgetInDom$: Observable<boolean>;
  subscription = new Subscription();
  darkBg$: Observable<boolean>;

  constructor() {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.widgetInDom$ = this.isOpenSubject$.pipe(keepState(STEP_CHANGE_TRANSITION_DURATION_MS));
  }

  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), finShare());
    this.darkBg$ = this.profile$.pipe(
      map((userProfile) =>
        (this.isNewBrand() ? this.NEW_BRAND_DARK_BACKGROUND_TIERS : this.LEGACY_DARK_BACKGROUND_TIERS).includes(
          userProfile?.tier
        )
      )
    );
    this.subscription.add(
      this.popoverService.popoverOpen$.pipe(filter(Boolean)).subscribe(() => {
        this.close();
      })
    );
  }

  private updateOpenState(isOpen: boolean): void {
    this.isOpenSubject$.next(isOpen);
    this.isOpenChange.emit(isOpen);
  }

  open(): void {
    this.popoverService.close();
    this.updateOpenState(true);
    addClass(this.document.body, 'overflow-hidden-tablet-down');
  }

  toggle(): void {
    if (this.isOpenSubject$.value) {
      this.close();
    } else {
      this.open();
    }
  }

  close(): void {
    this.updateOpenState(false);
    removeClass(this.document.body, 'overflow-hidden-tablet-down');
  }

  redirectToLogin(): void {
    if (this.configService.cfg.enableLoginDialog) {
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
      this.close();
    } else {
      this.loginService.redirectToLogin();
    }
  }

  redirectToJoin(): void {
    if (this.configService.cfg.enableJoinDialog) {
      this.gtmService.trackElement(
        `join-form-open-${onOpenGtmEvent}`,
        GaContext.FINNAIR_PLUS,
        ElementTypes.BUTTON,
        undefined,
        ElementActions.CLICK
      );

      this.joinService.joinDialogOpen$.next(true);
      this.close();
    } else {
      this.loginService.redirectToJoin();
    }
  }

  @HostListener('document:click', ['$event'])
  clickout(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }
}
